import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useCart } from '../../contexts/CartContext';
import { useAuth } from '../../contexts/AuthContext';
import orderService from '../../services/orderService';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import AppImage from '../../components/AppImage';
import Icon from '../../components/AppIcon';
import { CreditCard, Truck, CheckCircle, Lock, AlertCircle, Package } from 'lucide-react';
import notificationService from '../../utils/notificationService';

export default function AdvancedCheckoutSystem() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { cartItems, cartTotal, clearCart } = useCart();
  const [currentStep, setCurrentStep] = useState(1);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState('');

  // Get applied coupon from cart page
  const appliedCoupon = location?.state?.appliedCoupon || null;

  // Form data
  const [shippingInfo, setShippingInfo] = useState({
    fullName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    postalCode: '',
    notes: ''
  });

  const [paymentMethod, setPaymentMethod] = useState('credit-card');
  const [cardDetails, setCardDetails] = useState({
    cardNumber: '',
    cardName: '',
    expiryDate: '',
    cvv: ''
  });

  const [formErrors, setFormErrors] = useState({});
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  // Turkish cities
  const turkishCities = [
    'İstanbul', 'Ankara', 'İzmir', 'Antalya', 'Bursa', 'Adana', 'Gaziantep',
    'Konya', 'Mersin', 'Diyarbakır', 'Kayseri', 'Eskişehir', 'Muğla', 'Balıkesir',
    'Trabzon', 'Samsun', 'Denizli', 'Manisa', 'Aydın', 'Tekirdağ'
  ]?.sort();

  // Redirect if cart is empty
  useEffect(() => {
    if (!cartItems || cartItems?.length === 0) {
      notificationService?.warning('Sepetiniz boş');
      navigate('/official-store');
    }
  }, [cartItems, navigate]);

  // Redirect if not logged in
  useEffect(() => {
    if (!user) {
      notificationService?.info('Ödeme için giriş yapmalısınız');
      navigate('/login', { state: { from: '/advanced-checkout-system' } });
    }
  }, [user, navigate]);

  // Auto-fill shipping info from user metadata
  useEffect(() => {
    if (user) {
      const meta = user?.user_metadata || {};
      
      setShippingInfo(prev => ({
        ...prev,
        fullName: meta?.full_name || meta?.name || prev?.fullName,
        email: user?.email || meta?.email || prev?.email,
        phone: meta?.phone || meta?.gsm_number || meta?.phoneNumber || user?.phone || prev?.phone,
        city: meta?.city || meta?.shipping_address?.city || prev?.city,
        address: meta?.address || meta?.shipping_address?.address || meta?.full_address || prev?.address,
        postalCode: meta?.zip_code || meta?.postal_code || meta?.shipping_address?.zip_code || prev?.postalCode,
      }));
    }
  }, [user]);

  // Calculate totals
  const calculateShipping = () => {
    return cartTotal >= 500 ? 0 : 50;
  };

  const calculateTax = () => {
    return cartTotal * 0.20;
  };

  const calculateDiscount = () => {
    if (!appliedCoupon) return 0;
    if (appliedCoupon?.type === 'percentage') {
      return cartTotal * (appliedCoupon?.value / 100);
    }
    return appliedCoupon?.value;
  };

  const calculateFinalTotal = () => {
    const subtotal = cartTotal;
    const shipping = calculateShipping();
    const tax = calculateTax();
    const discount = calculateDiscount();
    return subtotal + shipping + tax - discount;
  };

  // Validate shipping info
  const validateShippingInfo = () => {
    const errors = {};

    if (!shippingInfo?.fullName?.trim()) {
      errors.fullName = 'Ad Soyad gerekli';
    }

    if (!shippingInfo?.email?.trim()) {
      errors.email = 'E-posta gerekli';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(shippingInfo?.email)) {
      errors.email = 'Geçerli bir e-posta adresi girin';
    }

    if (!shippingInfo?.phone?.trim()) {
      errors.phone = 'Telefon numarası gerekli';
    } else if (!/^[\d\s\-\+\(\)]+$/?.test(shippingInfo?.phone)) {
      errors.phone = 'Geçerli bir telefon numarası girin';
    }

    if (!shippingInfo?.address?.trim()) {
      errors.address = 'Adres gerekli';
    }

    if (!shippingInfo?.city) {
      errors.city = 'Şehir seçin';
    }

    if (!shippingInfo?.postalCode?.trim()) {
      errors.postalCode = 'Posta kodu gerekli';
    } else if (!/^\d{5}$/?.test(shippingInfo?.postalCode)) {
      errors.postalCode = 'Posta kodu 5 haneli olmalı';
    }

    setFormErrors(errors);
    return Object.keys(errors)?.length === 0;
  };

  // Validate payment info
  const validatePaymentInfo = () => {
    if (paymentMethod === 'bank-transfer') {
      return true; // No validation needed for bank transfer
    }

    const errors = {};

    if (!cardDetails?.cardNumber?.trim()) {
      errors.cardNumber = 'Kart numarası gerekli';
    } else if (!/^\d{16}$/?.test(cardDetails?.cardNumber?.replace(/\s/g, ''))) {
      errors.cardNumber = 'Geçerli bir kart numarası girin (16 hane)';
    }

    if (!cardDetails?.cardName?.trim()) {
      errors.cardName = 'Kart üzerindeki isim gerekli';
    }

    if (!cardDetails?.expiryDate?.trim()) {
      errors.expiryDate = 'Son kullanma tarihi gerekli';
    } else if (!/^(0[1-9]|1[0-2])\/\d{2}$/?.test(cardDetails?.expiryDate)) {
      errors.expiryDate = 'Format: AA/YY';
    }

    if (!cardDetails?.cvv?.trim()) {
      errors.cvv = 'CVV gerekli';
    } else if (!/^\d{3,4}$/?.test(cardDetails?.cvv)) {
      errors.cvv = 'CVV 3-4 haneli olmalı';
    }

    setFormErrors(errors);
    return Object.keys(errors)?.length === 0;
  };

  // Handle input change
  const handleInputChange = (section, field, value) => {
    if (section === 'shipping') {
      setShippingInfo(prev => ({ ...prev, [field]: value }));
    } else if (section === 'card') {
      setCardDetails(prev => ({ ...prev, [field]: value }));
    }

    // Clear error when user starts typing
    if (formErrors?.[field]) {
      setFormErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  // Handle next step
  const handleNextStep = () => {
    if (currentStep === 1) {
      if (validateShippingInfo()) {
        setCurrentStep(2);
        setError('');
      }
    } else if (currentStep === 2) {
      if (validatePaymentInfo()) {
        setCurrentStep(3);
        setError('');
      }
    }
  };

  // Handle place order
  const handlePlaceOrder = async () => {
    if (!agreedToTerms) {
      notificationService?.error('Lütfen kullanım koşullarını kabul edin');
      return;
    }

    setIsProcessing(true);
    setError('');

    try {
      // Prepare order data
      const orderData = {
        totalAmount: calculateFinalTotal(),
        shippingAddress: {
          full_name: shippingInfo?.fullName,
          email: shippingInfo?.email,
          phone: shippingInfo?.phone,
          address: shippingInfo?.address,
          city: shippingInfo?.city,
          postal_code: shippingInfo?.postalCode,
          notes: shippingInfo?.notes
        },
        paymentMethod: paymentMethod,
        cardDetails: paymentMethod === 'credit-card' ? {
          lastFourDigits: cardDetails?.cardNumber?.slice(-4),
          cardName: cardDetails?.cardName
        } : null,
        appliedCoupon: appliedCoupon
      };

      // Create order
      const result = await orderService?.createOrder(orderData, cartItems);

      // Clear cart
      clearCart();

      // Show success notification
      notificationService?.success('Siparişiniz başarıyla oluşturuldu!');

      // Redirect to order confirmation
      navigate(`/order-confirmation/${result?.id}`);
    } catch (err) {
      console.error('Order creation error:', err);
      setError(err?.message || 'Sipariş oluşturulurken bir hata oluştu');
      notificationService?.error(err?.message || 'Sipariş oluşturulamadı');
    } finally {
      setIsProcessing(false);
    }
  };

  // Step indicator component
  const StepIndicator = ({ step, label, isActive, isCompleted }) => (
    <div className="flex items-center">
      <div className={`flex items-center justify-center w-10 h-10 rounded-full border-2 ${
        isCompleted ? 'bg-green-500 border-green-500' : isActive ?'bg-blue-600 border-blue-600': 'bg-white border-gray-300'
      }`}>
        {isCompleted ? (
          <CheckCircle className="h-6 w-6 text-white" />
        ) : (
          <span className={`text-sm font-bold ${
            isActive ? 'text-white' : 'text-gray-500'
          }`}>
            {step}
          </span>
        )}
      </div>
      <span className={`ml-3 text-sm font-medium ${
        isActive ? 'text-gray-900' : 'text-gray-500'
      }`}>
        {label}
      </span>
    </div>
  );

  if (!user || !cartItems || cartItems?.length === 0) {
    return null; // Will redirect via useEffect
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Güvenli Ödeme</h1>
          <p className="text-gray-600">Siparişinizi tamamlamak için bilgilerinizi girin</p>
        </div>

        {/* Step Indicator */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
          <div className="flex items-center justify-between max-w-2xl mx-auto">
            <StepIndicator
              step={1}
              label="Teslimat Bilgileri"
              isActive={currentStep === 1}
              isCompleted={currentStep > 1}
            />
            <div className="flex-1 h-0.5 bg-gray-300 mx-4" />
            <StepIndicator
              step={2}
              label="Ödeme Yöntemi"
              isActive={currentStep === 2}
              isCompleted={currentStep > 2}
            />
            <div className="flex-1 h-0.5 bg-gray-300 mx-4" />
            <StepIndicator
              step={3}
              label="Sipariş Özeti"
              isActive={currentStep === 3}
              isCompleted={false}
            />
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="text-sm font-medium text-red-800">Hata</h3>
              <p className="text-sm text-red-700 mt-1">{error}</p>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            {/* Step 1: Shipping Information */}
            {currentStep === 1 && (
              <div className="bg-white rounded-lg shadow-sm p-6">
                <div className="flex items-center gap-3 mb-6">
                  <Truck className="h-6 w-6 text-blue-600" />
                  <h2 className="text-xl font-bold text-gray-900">Teslimat Bilgileri</h2>
                </div>

                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Ad Soyad *
                      </label>
                      <Input
                        type="text"
                        value={shippingInfo?.fullName}
                        onChange={(e) => handleInputChange('shipping', 'fullName', e?.target?.value)}
                        placeholder="Ahmet Yılmaz"
                        error={formErrors?.fullName}
                      />
                      {formErrors?.fullName && (
                        <p className="text-sm text-red-600 mt-1">{formErrors?.fullName}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        E-posta *
                      </label>
                      <Input
                        type="email"
                        value={shippingInfo?.email}
                        onChange={(e) => handleInputChange('shipping', 'email', e?.target?.value)}
                        placeholder="ahmet@example.com"
                        error={formErrors?.email}
                      />
                      {formErrors?.email && (
                        <p className="text-sm text-red-600 mt-1">{formErrors?.email}</p>
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Telefon *
                    </label>
                    <Input
                      type="tel"
                      value={shippingInfo?.phone}
                      onChange={(e) => handleInputChange('shipping', 'phone', e?.target?.value)}
                      placeholder="0555 123 45 67"
                      error={formErrors?.phone}
                    />
                    {formErrors?.phone && (
                      <p className="text-sm text-red-600 mt-1">{formErrors?.phone}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Adres *
                    </label>
                    <textarea
                      value={shippingInfo?.address}
                      onChange={(e) => handleInputChange('shipping', 'address', e?.target?.value)}
                      placeholder="Mahalle, sokak, bina no, daire no"
                      rows={3}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        formErrors?.address ? 'border-red-300' : 'border-gray-300'
                      }`}
                    />
                    {formErrors?.address && (
                      <p className="text-sm text-red-600 mt-1">{formErrors?.address}</p>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Şehir *
                      </label>
                      <select
                        value={shippingInfo?.city}
                        onChange={(e) => handleInputChange('shipping', 'city', e?.target?.value)}
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                          formErrors?.city ? 'border-red-300' : 'border-gray-300'
                        }`}
                      >
                        <option value="">Şehir seçin</option>
                        {turkishCities?.map(city => (
                          <option key={city} value={city}>{city}</option>
                        ))}
                      </select>
                      {formErrors?.city && (
                        <p className="text-sm text-red-600 mt-1">{formErrors?.city}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Posta Kodu *
                      </label>
                      <Input
                        type="text"
                        value={shippingInfo?.postalCode}
                        onChange={(e) => handleInputChange('shipping', 'postalCode', e?.target?.value)}
                        placeholder="34000"
                        maxLength={5}
                        error={formErrors?.postalCode}
                      />
                      {formErrors?.postalCode && (
                        <p className="text-sm text-red-600 mt-1">{formErrors?.postalCode}</p>
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Teslimat Notu (Opsiyonel)
                    </label>
                    <textarea
                      value={shippingInfo?.notes}
                      onChange={(e) => handleInputChange('shipping', 'notes', e?.target?.value)}
                      placeholder="Özel talimatlar veya notlar"
                      rows={2}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>

                <div className="mt-6 flex justify-end">
                  <Button onClick={handleNextStep} size="lg">
                    Devam Et
                  </Button>
                </div>
              </div>
            )}

            {/* Step 2: Payment Method */}
            {currentStep === 2 && (
              <div className="bg-white rounded-lg shadow-sm p-6">
                <div className="flex items-center gap-3 mb-6">
                  <CreditCard className="h-6 w-6 text-blue-600" />
                  <h2 className="text-xl font-bold text-gray-900">Ödeme Yöntemi</h2>
                </div>

                {/* Payment Method Selection */}
                <div className="space-y-4 mb-6">
                  <div
                    onClick={() => setPaymentMethod('credit-card')}
                    className={`border-2 rounded-lg p-4 cursor-pointer transition-colors ${
                      paymentMethod === 'credit-card' ?'border-blue-600 bg-blue-50' :'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        paymentMethod === 'credit-card' ?'border-blue-600' :'border-gray-300'
                      }`}>
                        {paymentMethod === 'credit-card' && (
                          <div className="w-3 h-3 rounded-full bg-blue-600" />
                        )}
                      </div>
                      <CreditCard className="h-5 w-5 text-gray-600" />
                      <span className="font-medium text-gray-900">Kredi/Banka Kartı</span>
                    </div>
                  </div>

                  <div
                    onClick={() => setPaymentMethod('bank-transfer')}
                    className={`border-2 rounded-lg p-4 cursor-pointer transition-colors ${
                      paymentMethod === 'bank-transfer' ?'border-blue-600 bg-blue-50' :'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        paymentMethod === 'bank-transfer' ?'border-blue-600' :'border-gray-300'
                      }`}>
                        {paymentMethod === 'bank-transfer' && (
                          <div className="w-3 h-3 rounded-full bg-blue-600" />
                        )}
                      </div>
                      <Icon name="Building" size={20} className="text-gray-600" />
                      <span className="font-medium text-gray-900">Havale/EFT</span>
                    </div>
                  </div>
                </div>

                {/* Credit Card Form */}
                {paymentMethod === 'credit-card' && (
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Kart Numarası *
                      </label>
                      <Input
                        type="text"
                        value={cardDetails?.cardNumber}
                        onChange={(e) => {
                          let value = e?.target?.value?.replace(/\s/g, '');
                          if (/^\d*$/?.test(value) && value?.length <= 16) {
                            handleInputChange('card', 'cardNumber', value);
                          }
                        }}
                        placeholder="1234 5678 9012 3456"
                        maxLength={16}
                        error={formErrors?.cardNumber}
                      />
                      {formErrors?.cardNumber && (
                        <p className="text-sm text-red-600 mt-1">{formErrors?.cardNumber}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Kart Üzerindeki İsim *
                      </label>
                      <Input
                        type="text"
                        value={cardDetails?.cardName}
                        onChange={(e) => handleInputChange('card', 'cardName', e?.target?.value)}
                        placeholder="AHMET YILMAZ"
                        error={formErrors?.cardName}
                      />
                      {formErrors?.cardName && (
                        <p className="text-sm text-red-600 mt-1">{formErrors?.cardName}</p>
                      )}
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Son Kullanma Tarihi *
                        </label>
                        <Input
                          type="text"
                          value={cardDetails?.expiryDate}
                          onChange={(e) => {
                            let value = e?.target?.value?.replace(/\D/g, '');
                            if (value?.length >= 2) {
                              value = value?.slice(0, 2) + '/' + value?.slice(2, 4);
                            }
                            if (value?.length <= 5) {
                              handleInputChange('card', 'expiryDate', value);
                            }
                          }}
                          placeholder="MM/YY"
                          maxLength={5}
                          error={formErrors?.expiryDate}
                        />
                        {formErrors?.expiryDate && (
                          <p className="text-sm text-red-600 mt-1">{formErrors?.expiryDate}</p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          CVV *
                        </label>
                        <Input
                          type="text"
                          value={cardDetails?.cvv}
                          onChange={(e) => {
                            let value = e?.target?.value?.replace(/\D/g, '');
                            if (value?.length <= 4) {
                              handleInputChange('card', 'cvv', value);
                            }
                          }}
                          placeholder="123"
                          maxLength={4}
                          error={formErrors?.cvv}
                        />
                        {formErrors?.cvv && (
                          <p className="text-sm text-red-600 mt-1">{formErrors?.cvv}</p>
                        )}
                      </div>
                    </div>

                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
                      <Lock className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-blue-900">Güvenli Ödeme</p>
                        <p className="text-sm text-blue-700 mt-1">
                          Ödeme bilgileriniz 256-bit SSL şifreleme ile korunmaktadır.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Bank Transfer Info */}
                {paymentMethod === 'bank-transfer' && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <p className="text-sm font-medium text-yellow-900 mb-2">
                      Havale/EFT Bilgileri
                    </p>
                    <p className="text-sm text-yellow-800">
                      Sipariş onaylandıktan sonra banka hesap bilgilerimiz e-posta ile gönderilecektir.
                      Ödeme yapıldıktan sonra siparişiniz işleme alınacaktır.
                    </p>
                  </div>
                )}

                <div className="mt-6 flex justify-between">
                  <Button
                    onClick={() => setCurrentStep(1)}
                    variant="outline"
                    size="lg"
                  >
                    Geri
                  </Button>
                  <Button onClick={handleNextStep} size="lg">
                    Devam Et
                  </Button>
                </div>
              </div>
            )}

            {/* Step 3: Order Review */}
            {currentStep === 3 && (
              <div className="space-y-6">
                {/* Order Items */}
                <div className="bg-white rounded-lg shadow-sm p-6">
                  <h2 className="text-xl font-bold text-gray-900 mb-4">Sipariş Özeti</h2>
                  <div className="space-y-4">
                    {cartItems?.map((item) => (
                      <div key={item?.id} className="flex gap-4 pb-4 border-b last:border-0">
                        <div className="w-20 h-20 flex-shrink-0 rounded-lg overflow-hidden bg-gray-100">
                          {item?.image ? (
                            <AppImage
                              src={item?.image}
                              alt={item?.imageAlt || `${item?.title} product image`}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <Package className="h-8 w-8 text-gray-400" />
                            </div>
                          )}
                        </div>
                        <div className="flex-1">
                          <h4 className="font-medium text-gray-900">{item?.title}</h4>
                          <p className="text-sm text-gray-500 mt-1">Miktar: {item?.quantity}</p>
                          <p className="text-sm font-medium text-gray-900 mt-1">
                            {item?.price?.toLocaleString('tr-TR')} ₺ × {item?.quantity}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-gray-900">
                            {(item?.price * item?.quantity)?.toLocaleString('tr-TR')} ₺
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Shipping Info Review */}
                <div className="bg-white rounded-lg shadow-sm p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-4">Teslimat Bilgileri</h3>
                  <div className="space-y-2 text-sm">
                    <p><span className="font-medium">Ad Soyad:</span> {shippingInfo?.fullName}</p>
                    <p><span className="font-medium">E-posta:</span> {shippingInfo?.email}</p>
                    <p><span className="font-medium">Telefon:</span> {shippingInfo?.phone}</p>
                    <p><span className="font-medium">Adres:</span> {shippingInfo?.address}</p>
                    <p><span className="font-medium">Şehir:</span> {shippingInfo?.city} {shippingInfo?.postalCode}</p>
                    {shippingInfo?.notes && (
                      <p><span className="font-medium">Not:</span> {shippingInfo?.notes}</p>
                    )}
                  </div>
                  <Button
                    onClick={() => setCurrentStep(1)}
                    variant="outline"
                    size="sm"
                    className="mt-4"
                  >
                    Düzenle
                  </Button>
                </div>

                {/* Payment Method Review */}
                <div className="bg-white rounded-lg shadow-sm p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-4">Ödeme Yöntemi</h3>
                  <div className="flex items-center gap-3">
                    {paymentMethod === 'credit-card' ? (
                      <>
                        <CreditCard className="h-5 w-5 text-gray-600" />
                        <span className="text-sm">Kredi/Banka Kartı (**** {cardDetails?.cardNumber?.slice(-4)})</span>
                      </>
                    ) : (
                      <>
                        <Icon name="Building" size={20} className="text-gray-600" />
                        <span className="text-sm">Havale/EFT</span>
                      </>
                    )}
                  </div>
                  <Button
                    onClick={() => setCurrentStep(2)}
                    variant="outline"
                    size="sm"
                    className="mt-4"
                  >
                    Düzenle
                  </Button>
                </div>

                {/* Terms Agreement */}
                <div className="bg-white rounded-lg shadow-sm p-6">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={agreedToTerms}
                      onChange={(e) => setAgreedToTerms(e?.target?.checked)}
                      className="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">
                      <span className="font-medium">Kullanım koşullarını</span> ve{' '}
                      <span className="font-medium">gizlilik politikasını</span> okudum ve kabul ediyorum.
                    </span>
                  </label>
                </div>

                {/* Place Order Button */}
                <div className="flex justify-between">
                  <Button
                    onClick={() => setCurrentStep(2)}
                    variant="outline"
                    size="lg"
                  >
                    Geri
                  </Button>
                  <Button
                    onClick={handlePlaceOrder}
                    disabled={isProcessing || !agreedToTerms}
                    size="lg"
                    className="flex items-center gap-2"
                  >
                    {isProcessing ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        İşleniyor...
                      </>
                    ) : (
                      <>
                        <Lock className="h-5 w-5" />
                        Siparişi Tamamla
                      </>
                    )}
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Order Summary Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-sm p-6 sticky top-4">
              <h2 className="text-xl font-bold text-gray-900 mb-6">Sipariş Toplamı</h2>

              <div className="space-y-3 mb-6">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Ara Toplam</span>
                  <span className="font-medium text-gray-900">
                    {cartTotal?.toLocaleString('tr-TR')} ₺
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Kargo</span>
                  <span className="font-medium text-gray-900">
                    {calculateShipping() === 0 ? (
                      <span className="text-green-600">Ücretsiz</span>
                    ) : (
                      `${calculateShipping()?.toLocaleString('tr-TR')} ₺`
                    )}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">KDV (%20)</span>
                  <span className="font-medium text-gray-900">
                    {calculateTax()?.toLocaleString('tr-TR')} ₺
                  </span>
                </div>
                {appliedCoupon && calculateDiscount() > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-green-600">İndirim ({appliedCoupon?.code})</span>
                    <span className="font-medium text-green-600">
                      -{calculateDiscount()?.toLocaleString('tr-TR')} ₺
                    </span>
                  </div>
                )}
              </div>

              <div className="flex justify-between items-center pt-6 border-t border-gray-200 mb-6">
                <span className="text-lg font-bold text-gray-900">Toplam</span>
                <span className="text-2xl font-bold text-gray-900">
                  {calculateFinalTotal()?.toLocaleString('tr-TR')} ₺
                </span>
              </div>

              <div className="space-y-2 pt-4 border-t border-gray-200">
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Lock className="h-4 w-4 text-green-600" />
                  <span>SSL Güvenli Ödeme</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Truck className="h-4 w-4 text-blue-600" />
                  <span>3-5 İş Günü Teslimat</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  <span>14 Gün İade Garantisi</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}